import { useCallback, useEffect } from 'react'

import { useQuery } from '@fv/client-core'
import type { ClientPackagingType } from '@fv/client-types'
import type {
  CarrierSettings,
  LocationAccessorialType,
  Workflow,
} from '@fv/models'
import type {
  EquipmentAccessorialType,
  EquipmentType,
} from '@fv/models/equipment'

import { useRole } from '@/features/auth/hooks'
import { useVendorSettings } from '@/features/vendor/queries'
import type { EquipmentTypeEnum } from '@/types'
import type { ApiError } from '@/types/ApiError'
import { alphabeticalSorter } from '@/utils/alphabeticalSorter'
import { shipperFetch } from '@/utils/shipperFetch'

import { useUserSettings } from '.'
import type { AccessorialTypeEnum } from './useAccessorialOptions'
import { useAppSettings } from './useAppSettings'

export const quoteSettingsKey = 'quote-settings'

type BasePackage = {
  canBeContained?: boolean
  containsFluid?: boolean
  isContainer?: boolean
  isFluid?: boolean
  key: ClientPackagingType
}

export type PackageOption = BasePackage & { name: string }

type RecentSpotQuoteGroup = {
  groupId: string
}

type MappedQuoteSettings = {
  accessorialOptions: AccessorialTypeEnum[]
  apiEnabledCarriers: CarrierSettings[]
  equipmentOptions: EquipmentTypeEnum[]
  packageOptions: Array<PackageOption>
  recentSpotQuoteGroups: RecentSpotQuoteGroup[]
}

export type QuoteSettings = {
  accessorialTypes: Array<{
    favorite: boolean
    key: EquipmentAccessorialType | LocationAccessorialType
  }>
  apiEnabledCarriers: string[]
  equipmentTypes: Array<{
    favorite: boolean
    key: EquipmentType
  }>
  packageTypes: Array<BasePackage & { favorite: boolean }>
  recentSpotQuoteGroups?: RecentSpotQuoteGroup[]
}

async function fetchQuoteSettings(workflow: Workflow): Promise<QuoteSettings> {
  return shipperFetch<QuoteSettings>(`/quote-settings/${workflow}`)
}

export function useQuoteSettings(workflow: Workflow) {
  const appSettingsQuery = useAppSettings()
  const userSettingsQuery = useUserSettings()
  const role = useRole()
  const vendorSettings = useVendorSettings()

  const mapToAppSettings = useCallback(
    (data: QuoteSettings): MappedQuoteSettings => {
      const {
        accessorialTypes,
        equipmentTypes,
        packageTypes,
        recentSpotQuoteGroups = [],
      } = data

      const availableEquipmentTypes =
        role === 'vendor'
          ? equipmentTypes.filter(t =>
              vendorSettings.data?.equipmentOptions?.some(e => e === t.key),
            )
          : equipmentTypes
      const carriers = userSettingsQuery.data?.carriers ?? []
      const enums = appSettingsQuery.data?.enums

      const accessorialOptions = accessorialTypes
        .map(t => {
          const accessorial = enums?.accessorialTypes?.find(
            e => e.mode === workflow && e.key === t.key,
          )

          if (!accessorial) return null

          return {
            key: t.key,
            name: accessorial?.name ?? t.key,
            prompts: accessorial?.prompts ?? [],
            type: accessorial?.type,
          }
        })
        .filter(o => !!o) as AccessorialTypeEnum[]

      const equipmentOptions = availableEquipmentTypes
        .map(t => {
          const equipment = enums?.equipmentTypes?.find(e => e.key === t.key)
          if (!equipment) return null

          return {
            accessorialOptions: (equipment.accessorialTypes ?? []).map(key => {
              const accessorial = enums?.accessorialTypes?.find(
                e => e.mode === workflow && e.key === key,
              )

              return {
                key,
                name: accessorial?.name ?? key,
                prompts: accessorial?.prompts ?? [],
                type: accessorial?.type,
              }
            }),
            favorite: t.favorite,
            key: t.key,
            name: equipment.name ?? t.key,
          }
        })
        .filter(o => !!o)
        .sort(alphabeticalSorter('name')) as EquipmentTypeEnum[]

      const packageOptions = packageTypes.map(({ favorite, ...pkg }) => {
        const type = enums?.packagingTypeList?.find(e => e.key === pkg.key)

        return {
          ...pkg,
          name: type?.name ?? pkg.key,
        }
      })

      return {
        accessorialOptions,
        apiEnabledCarriers: data.apiEnabledCarriers
          .map(id => carriers.find(c => c._id === id))
          .filter(c => !!c) as CarrierSettings[],
        equipmentOptions,
        packageOptions,
        recentSpotQuoteGroups,
      }
    },
    [
      appSettingsQuery.data?.enums,
      userSettingsQuery.data?.carriers,
      workflow,
      role,
      vendorSettings.data,
    ],
  )

  const query = useQuery<QuoteSettings, ApiError, MappedQuoteSettings>(
    [quoteSettingsKey, workflow],
    () => fetchQuoteSettings(workflow),
    {
      enabled: Boolean(workflow),
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      select: mapToAppSettings,
      staleTime: Infinity,
    },
  )

  // App is unusable without quote settings, throw to ErrorBoundary
  useEffect(() => {
    if (query.error && query.error.status !== 401) {
      throw new Error(query.error.message)
    }
  }, [query.error])

  return {
    ...query,
    isLoading:
      (query.isLoading && query.isFetching) || appSettingsQuery.isLoading,
  }
}
